import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { fetchWithAuth } from '../utils/apiUtils';
import { toast } from 'react-toastify';
import { API_URL } from '../config';

// Create the context
const TrainerContext = createContext();

// Custom hook for using the Trainer context
export const useTrainer = () => {
  const context = useContext(TrainerContext);
  if (!context) {
    throw new Error('useTrainer must be used within a TrainerProvider');
  }
  return context;
};

// Trainer Provider component
export const TrainerProvider = ({ children }) => {
  const [isTrainer, setIsTrainer] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersPagination, setMembersPagination] = useState({
    skip: 0,
    limit: 10,
    total: 0
  });

  const API_BASE_URL = API_URL;
  const initialCheckDoneRef = useRef(false);

  // Set initial trainer status from localStorage on mount
  useEffect(() => {
    if (!initialCheckDoneRef.current) {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          if (user.user_type === 'trainer') {
            setIsTrainer(true);
          } else {
            setIsTrainer(false);
          }
        } else {
          setIsTrainer(false);
        }
      } catch (err) {
        console.error("Error setting initial trainer status:", err);
        setIsTrainer(false);
      }
      initialCheckDoneRef.current = true;
      setIsLoading(false);
    }
  }, []);

  // Fetch trainer dashboard stats
  const fetchDashboardStats = async (forceRefresh = false) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/trainer/dashboard-stats`);
      
      console.log('response dashboard', response);
      // fetchWithAuth returns { error: boolean, data: any, status: number }
      if (response.error) {
        throw new Error(response.message || 'Failed to fetch dashboard stats');
      }
      
      const data = response.data || response;
      setDashboardStats(data);
      return data;
    } catch (error) {
      console.error('Error fetching trainer dashboard stats:', error);
      toast.error('Failed to load dashboard statistics');
      throw error;
    }
  };

  // Fetch trainer members
  const fetchMembers = async (skip = 0, limit = 10, search = '') => {
    try {
      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString()
      });
      if (search) {
        params.append('search', search);
      }
      
      const response = await fetchWithAuth(`${API_BASE_URL}/trainer/members?${params}`);
      
      // fetchWithAuth returns { error: boolean, data: any, status: number }
      if (response.error) {
        throw new Error(response.message || 'Failed to fetch members');
      }
      
      const data = response.data || response;
      setMembers(data.members || []);
      setMembersPagination({
        skip: data.skip || 0,
        limit: data.limit || 10,
        total: data.total || 0
      });
      return data;
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error('Failed to load members');
      throw error;
    }
  };

  // Fetch member details
  const fetchMemberDetails = async (memberId) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/trainer/members/${memberId}`);
      
      // fetchWithAuth returns { error: boolean, data: any, status: number }
      if (response.error) {
        throw new Error(response.message || 'Failed to fetch member details');
      }
      
      return response.data || response;
    } catch (error) {
      console.error('Error fetching member details:', error);
      toast.error('Failed to load member details');
      throw error;
    }
  };

  // Fetch member progress
  const fetchMemberProgress = async (memberId, period = 'month') => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/trainer/members/${memberId}/progress?period=${period}`);
      
      // fetchWithAuth returns { error: boolean, data: any, status: number }
      if (response.error) {
        throw new Error(response.message || 'Failed to fetch member progress');
      }
      
      return response.data || response;
    } catch (error) {
      console.error('Error fetching member progress:', error);
      toast.error('Failed to load member progress');
      throw error;
    }
  };

  // Fetch member sessions
  const fetchMemberSessions = async (memberId, skip = 0, limit = 20) => {
    try {
      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString()
      });
      
      const response = await fetchWithAuth(`${API_BASE_URL}/trainer/members/${memberId}/sessions?${params}`);
      
      // fetchWithAuth returns { error: boolean, data: any, status: number }
      if (response.error) {
        throw new Error(response.message || 'Failed to fetch member sessions');
      }
      
      return response.data || response;
    } catch (error) {
      console.error('Error fetching member sessions:', error);
      toast.error('Failed to load member sessions');
      throw error;
    }
  };

  // Fetch trainer analytics
  const fetchAnalytics = async (period = 'month', memberId = null) => {
    try {
      let url = `${API_BASE_URL}/trainer/analytics?period=${period}`;
      if (memberId) {
        url += `&member_id=${memberId}`;
      }
      const response = await fetchWithAuth(url);
      
      // fetchWithAuth returns { error: boolean, data: any, status: number }
      if (response.error) {
        throw new Error(response.message || 'Failed to fetch analytics');
      }
      
      return response.data || response;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
      throw error;
    }
  };

  // Get workout plan for editing
  const getWorkoutPlan = async (planId) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/trainer/workout-plans/${planId}`);
      
      if (response.error) {
        throw new Error(response.message || 'Failed to fetch workout plan');
      }
      
      return response.data || response;
    } catch (error) {
      console.error('Error fetching workout plan:', error);
      toast.error('Failed to load workout plan');
      throw error;
    }
  };

  // Update workout plan
  const updateWorkoutPlan = async (planId, planData) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/trainer/workout-plans/${planId}`, {
        method: 'PUT',
        body: JSON.stringify(planData),
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.error) {
        throw new Error(response.message || 'Failed to update workout plan');
      }
      
      toast.success('Workout plan updated successfully');
      return response.data || response;
    } catch (error) {
      console.error('Error updating workout plan:', error);
      toast.error('Failed to update workout plan');
      throw error;
    }
  };

  // Get diet plan for editing
  const getDietPlan = async (planId) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/trainer/diet-plans/${planId}`);
      
      if (response.error) {
        throw new Error(response.message || 'Failed to fetch diet plan');
      }
      
      return response.data || response;
    } catch (error) {
      console.error('Error fetching diet plan:', error);
      toast.error('Failed to load diet plan');
      throw error;
    }
  };

  // Update diet plan
  const updateDietPlan = async (planId, planData) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/trainer/diet-plans/${planId}`, {
        method: 'PUT',
        body: JSON.stringify(planData),
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.error) {
        throw new Error(response.message || 'Failed to update diet plan');
      }
      
      toast.success('Diet plan updated successfully');
      return response.data || response;
    } catch (error) {
      console.error('Error updating diet plan:', error);
      toast.error('Failed to update diet plan');
      throw error;
    }
  };

  const value = {
    isTrainer,
    isLoading,
    dashboardStats,
    members,
    membersPagination,
    fetchDashboardStats,
    fetchMembers,
    fetchMemberDetails,
    fetchMemberProgress,
    fetchMemberSessions,
    fetchAnalytics,
    getWorkoutPlan,
    updateWorkoutPlan,
    getDietPlan,
    updateDietPlan
  };

  return (
    <TrainerContext.Provider value={value}>
      {children}
    </TrainerContext.Provider>
  );
};

